// A send-ready email an agent wrote, shown in place: read, edited, then made a draft
// in the operator's own Gmail - a reply-all on the latest message of the
// conversation it answers, on their copy of it when they were on it. Whatever the card shows when the
// button is pressed is what the draft holds. See
// docs/Y2026-Q4/design-gmail-reply-drafts.md.
import { useContext, useEffect, useState } from "react";

import { UserContext } from "../auth/UserContext";
import { ensureGsiLoaded, GoogleAuthError } from "../api/google-auth";
import {
  copyLine,
  type DraftPlacement,
  type ReplyContext,
  type SavedGmailDraft,
  saveGmailDraft,
} from "../api/gmailDrafts";
import { useReplyContext } from "../api/hooks/useReplyContext";
import { emailAddresses, gmailDraftUrl } from "../utils/gmailLinks";
import styles from "./EmailDraft.module.css";

export interface EmailText {
  to: string;
  subject: string;
  body: string;
  /** The upload id of the email thread it replies on; "" for a new conversation. */
  thread: string;
}

class ThreadUnavailable extends Error {}

const PLACED: Record<DraftPlacement, (account: string) => string> = {
  thread: (a) => `Saved as a reply on the thread in ${a}'s Drafts.`,
  new: (a) => `Saved in ${a}'s Drafts.`,
  not_on_thread: (a) =>
    `${a} is not on this thread, so it was saved as a new message in Drafts.`,
  no_ids: (a) =>
    `This thread was stored without the ids Gmail threads by, so it was saved as a new message in ${a}'s Drafts.`,
  split: (a) =>
    `Saved in ${a}'s Drafts, but Gmail started a new conversation for it - a changed subject does that.`,
};

export const EmailDraft = ({
  draft,
  draftId,
}: {
  draft: EmailText;
  /** The agent's draft id: a second press updates the same Gmail draft. */
  draftId: string;
}) => {
  const { user } = useContext(UserContext);
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState(draft.to);
  const [body, setBody] = useState(draft.body);
  // The operator's own Subject and Cc; null until they type one, and the default
  // (from the thread, else the agent's) stands.
  const [subjectEdit, setSubjectEdit] = useState<string | null>(null);
  const [ccEdit, setCcEdit] = useState<string | null>(null);
  // The exact text on the clipboard, so the button reports the CURRENT message
  // as copied rather than the one copied before the operator carried on editing.
  const [copied, setCopied] = useState<string | null>(null);
  const [copyError, setCopyError] = useState("");
  const [wantContext, setWantContext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<SavedGmailDraft | null>(null);
  const [saveError, setSaveError] = useState("");
  const [threadFailed, setThreadFailed] = useState(false);

  const context = useReplyContext(draft.thread, wantContext);
  // Only once this card asked: another card on the same thread may have it cached.
  const shown = wantContext ? context.data : undefined;

  // Defaults derived from what the card holds NOW, so a To edited after the thread
  // loaded still leaves everyone else on Cc.
  const subjectFor = (ctx: ReplyContext | null | undefined) =>
    subjectEdit ?? (ctx?.subject || draft.subject);
  const ccFor = (ctx: ReplyContext | null | undefined) =>
    ccEdit ?? (ctx ? copyLine(ctx.participants, to, [user?.email]) : "");
  const subject = subjectFor(shown);
  const cc = ccFor(shown);

  // A later version from the agent replaces the operator's unsent edits.
  useEffect(() => {
    setTo(draft.to);
    setBody(draft.body);
    setSubjectEdit(null);
    setCcEdit(null);
    setCopied(null);
    setCopyError("");
  }, [draft.to, draft.subject, draft.body]);

  // Loaded ahead of the click, so the click's permission to open Google's consent
  // popup is not spent waiting on the script. A failure here surfaces on the click.
  useEffect(() => {
    ensureGsiLoaded().catch(() => undefined);
  }, []);

  const isCopied = copied !== null && copied === body;
  // A reply's Cc comes from the thread, so it may have no address of its own yet.
  const canDraft = !!draft.thread || emailAddresses(`${to} ${cc}`).length > 0;
  const edited = to !== draft.to || subject !== draft.subject || cc !== "";

  const copy = () => {
    navigator.clipboard.writeText(body).then(
      () => {
        setCopied(body);
        setCopyError("");
      },
      () =>
        setCopyError(
          "Could not reach the clipboard - select the text and copy.",
        ),
    );
  };

  const edit = () => {
    if (!editing) setWantContext(true);
    setEditing(!editing);
  };

  const makeDraft = async (asNewMessage: boolean) => {
    if (saving || !canDraft) return;
    setSaving(true);
    setSaveError("");
    try {
      const done = await saveGmailDraft({
        cardId: draftId,
        loadContext: async () => {
          if (!draft.thread || asNewMessage) return null;
          setWantContext(true);
          const result = context.data ? context : await context.refetch();
          if (!result.data) {
            throw new ThreadUnavailable(
              `Could not load the email thread, so no draft was made: ${
                (result.error as Error | null)?.message ?? "no answer"
              }`,
            );
          }
          return result.data;
        },
        fieldsFor: (ctx) => ({
          to,
          cc: ccFor(ctx),
          subject: subjectFor(ctx),
          body,
        }),
      });
      setSaved(done);
      setThreadFailed(false);
      const tab = window.open(gmailDraftUrl(done.messageId, done.account), "_blank");
      if (tab) tab.opener = null;
    } catch (err) {
      if (err instanceof ThreadUnavailable) setThreadFailed(true);
      setSaveError(
        err instanceof GoogleAuthError && err.isUserCancelled
          ? "Google sign-in was closed or blocked - press again, allowing pop-ups for this site if asked."
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setSaving(false);
    }
  };

  const status =
    saveError ||
    copyError ||
    (wantContext && context.error
      ? `Could not load the email thread: ${(context.error as Error).message}`
      : "") ||
    (saving
      ? "Making the Gmail draft..."
      : saved
        ? PLACED[saved.placement](saved.account)
        : draft.thread
          ? "Replies to all on the email thread's latest message."
          : "");

  return (
    <div className={styles.draft}>
      {editing ? (
        <div className={styles.fields}>
          <label className={styles.field}>
            <span className={styles.label}>To</span>
            <input
              type="text"
              value={to}
              placeholder="no address on the counterparty roster yet"
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Cc</span>
            <input
              type="text"
              value={cc}
              placeholder={
                draft.thread && context.isLoading
                  ? "loading the thread's latest message..."
                  : ""
              }
              onChange={(e) => setCcEdit(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubjectEdit(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Message</span>
            <textarea
              value={body}
              spellCheck
              onChange={(e) => setBody(e.target.value)}
            />
          </label>
        </div>
      ) : (
        <>
          {edited && (
            <p className={styles.edited}>
              To: {to || "no recipient"}
              {cc && <> · Cc: {cc}</>} · Subject: {subject || "(no subject)"}
            </p>
          )}
          <pre className={styles.body}>{body}</pre>
        </>
      )}
      <div className={styles.foot}>
        <button
          type="button"
          className={`${styles.btn} ${styles.primary} ${
            saving || !canDraft ? styles.disabled : ""
          }`}
          onClick={() => makeDraft(false)}
          aria-disabled={saving || !canDraft}
        >
          {!canDraft
            ? "Create draft in Gmail - no address"
            : saved
              ? "Update the Gmail draft"
              : "Create draft in Gmail"}
        </button>
        {(threadFailed || (wantContext && !!context.error)) && (
          <button
            type="button"
            className={styles.btn}
            onClick={() => makeDraft(true)}
          >
            Create as a new message instead
          </button>
        )}
        {saved && (
          <a
            className={styles.btn}
            href={gmailDraftUrl(saved.messageId, saved.account)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the draft
          </a>
        )}
        <button
          type="button"
          className={styles.btn}
          onClick={copy}
          disabled={isCopied}
        >
          {isCopied ? "Message copied" : "Copy the message"}
        </button>
        <button type="button" className={styles.btn} onClick={edit}>
          {editing ? "Done editing" : "Edit"}
        </button>
        <span className={styles.status}>{status}</span>
      </div>
    </div>
  );
};
